/* Spiderfy open/collapse state machine (toolkit map-stacking port, #854).
   Owns WHEN a stack fans out and every path back to collapsed — badge
   re-click, outside map click, the anchor scrolling or zooming out of view —
   plus the marker-pane fade while open. The Leaflet layer work (fan markers,
   connector legs, badge swap) is injected through hooks, so this file tests
   against a fake map without booting Leaflet (zoomDamping precedent). */

type MapEvents = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, fn: (...args: any[]) => void): unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  off(event: string, fn: (...args: any[]) => void): unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  once(event: string, fn: (...args: any[]) => void): unknown
}

export type SpiderfyHooks = {
  /** Build the fan (center dot, leaves, legs) for a stack. Returns a teardown
      that removes every layer it created, or null when there is nothing to fan
      (unknown key, empty group) — the controller then stays collapsed. */
  renderFan: (key: string) => (() => void) | null
  /** Zoom ended with the anchor still in view: recompute leaf/leg positions
      (pixel-radius geometry changes meaning per zoom level). */
  repositionFan: (key: string) => void
  /** Whether the stack's anchor is inside the current map bounds. */
  isAnchorVisible: (key: string) => boolean
  /** Fade the other markers while a fan is open; null restores. */
  setFade: (opacity: number | null) => void
  /** Swap the stacked badge out (open) / back in (close). */
  onOpen?: (key: string) => void
  onClose?: (key: string) => void
}

export type SpiderfyController = {
  toggle: (key: string) => void
  collapse: () => void
  openKey: () => string | null
  dispose: () => void
}

export function createSpiderfyController(
  map: MapEvents,
  fadeOpacity: number,
  hooks: SpiderfyHooks,
): SpiderfyController {
  let openKey: string | null = null
  let teardown: (() => void) | null = null

  const onOutsideClick = () => collapse()

  const onMoveEnd = () => {
    // A centered pan (card hover on touch, programmatic framing) fires moveend
    // without zoomend — collapse when the anchor left the viewport rather than
    // clinging off-screen with the dim still applied.
    if (openKey && !hooks.isAnchorVisible(openKey)) collapse()
  }

  const onZoomEnd = () => {
    if (!openKey) return
    if (!hooks.isAnchorVisible(openKey)) {
      collapse()
      return
    }
    hooks.repositionFan(openKey)
  }

  function collapse() {
    if (!openKey) return
    const key = openKey
    openKey = null
    teardown?.()
    teardown = null
    hooks.setFade(null)
    map.off('click', onOutsideClick)
    map.off('moveend', onMoveEnd)
    map.off('zoomend', onZoomEnd)
    hooks.onClose?.(key)
  }

  function toggle(key: string) {
    if (openKey === key) {
      collapse()
      return
    }
    collapse()
    const t = hooks.renderFan(key)
    if (!t) return
    teardown = t
    openKey = key
    hooks.onOpen?.(key)
    hooks.setFade(fadeOpacity)
    map.once('click', onOutsideClick)
    map.on('moveend', onMoveEnd)
    map.on('zoomend', onZoomEnd)
  }

  return {
    toggle,
    collapse,
    openKey: () => openKey,
    dispose: collapse,
  }
}
